
import React, { useState } from 'react';
import { UserProfile, Professional, Appointment } from '../../../app/domain/types/index.ts';
import { Search, Filter, Star, MapPin, Video, Phone, Calendar, Clock, ChevronLeft, ShieldCheck, CheckCircle, CreditCard, User } from 'lucide-react';

interface ProfessionalServicesProps {
  user: UserProfile;
}

// Mock Professionals Data
const professionalsDb: Professional[] = [
  {
    id: 'p1',
    name: 'Dr. James Smith',
    title: 'Endocrinologist',
    image: 'JS',
    specialties: ['Type 2 Diabetes', 'Hormonal Balance'],
    rating: 4.9,
    reviewCount: 127,
    pricePerSession: 150,
    nextAvailable: 'Tomorrow, 2:00 PM',
    verified: true,
    languages: ['English', 'Spanish'],
    education: 'MD from Harvard Medical School',
    bio: 'Dr. Smith has over 15 years of experience treating metabolic disorders. He takes a holistic approach, combining medical treatment with lifestyle changes.'
  },
  {
    id: 'p2',
    name: 'Sarah Johnson, RD',
    title: 'Registered Dietitian',
    image: 'SJ',
    specialties: ['Weight Loss', 'Gut Health', 'Meal Planning'],
    rating: 4.8,
    reviewCount: 89,
    pricePerSession: 95,
    nextAvailable: 'Today, 4:30 PM',
    verified: true,
    languages: ['English'],
    education: 'MS in Nutrition from UT Austin',
    bio: 'Sarah specializes in sustainable weight loss strategies that do not rely on restrictive dieting. She works with clients to build healthy habits that last.'
  },
  {
    id: 'p3',
    name: 'Dr. Emily Chen',
    title: 'Clinical Psychologist',
    image: 'EC',
    specialties: ['Eating Disorders', 'Stress Management'],
    rating: 4.9,
    reviewCount: 56,
    pricePerSession: 175,
    nextAvailable: 'Wed, 10:00 AM',
    verified: true,
    languages: ['English', 'Mandarin'],
    education: 'PhD in Psychology from Stanford',
    bio: 'Dr. Chen helps patients address the emotional aspects of eating and weight management. She uses CBT and mindfulness techniques.'
  }
];

// Mock Appointments
const mockAppointments: Appointment[] = [
  {
    id: 'a1',
    professionalId: 'p1',
    professionalName: 'Dr. James Smith',
    professionalTitle: 'Endocrinologist',
    professionalImage: 'JS',
    date: '2025-12-10',
    time: '2:00 PM',
    duration: 30,
    type: 'video',
    status: 'upcoming'
  },
  {
    id: 'a2',
    professionalId: 'p2',
    professionalName: 'Sarah Johnson, RD',
    professionalTitle: 'Registered Dietitian',
    professionalImage: 'SJ',
    date: '2025-11-15',
    time: '10:00 AM',
    duration: 60,
    type: 'video',
    status: 'completed'
  }
];

const ProfessionalServices: React.FC<ProfessionalServicesProps> = ({ user }) => {
  const [view, setView] = useState<'directory' | 'profile' | 'booking' | 'appointments'>('directory');
  const [selectedPro, setSelectedPro] = useState<Professional | null>(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const handleBookClick = (pro: Professional) => {
    setSelectedPro(pro);
    setView('booking');
  };

  const handleProClick = (pro: Professional) => {
    setSelectedPro(pro);
    setView('profile');
  };

  const filteredPros = professionalsDb.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = activeFilter === 'All' || p.specialties.includes(activeFilter) || p.title.includes(activeFilter);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Header */}
      <div className="p-6 md:p-8 pb-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md sticky top-0 z-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4">
            <div>
                <h1 className="text-3xl font-light tracking-tight text-gray-800 dark:text-white">Professional Services</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Connect with certified experts for personalized care.
                </p>
            </div>
            <div className="flex gap-3">
                 <button
                    onClick={() => setView('appointments')}
                    className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition-colors ${view === 'appointments' ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-gray-700 dark:text-gray-200 hover:bg-stone-50 dark:hover:bg-stone-700'}`}
                 >
                    <Calendar size={18} /> My Appointments
                 </button>
                 <button
                    onClick={() => setView('directory')}
                    className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition-colors ${view === 'directory' ? 'bg-health-600 text-white shadow-lg shadow-health-600/20' : 'bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-gray-700 dark:text-gray-200 hover:bg-stone-50 dark:hover:bg-stone-700'}`}
                 >
                    <Search size={18} /> Find Expert
                 </button>
            </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 md:p-8 no-scrollbar pb-24">

        {/* VIEW: Directory */}
        {view === 'directory' && (
            <div className="animate-fade-in-up">
                {/* Search & Filter Bar */}
                <div className="mb-8 flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by name, specialty, or condition..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl shadow-sm focus:outline-none focus:border-health-500 transition-colors text-gray-800 dark:text-white"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                        {['All', 'Dietitian', 'Endocrinologist', 'Psychologist', 'Coach'].map(filter => (
                            <button
                                key={filter}
                                onClick={() => setActiveFilter(filter)}
                                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
                                    activeFilter === filter 
                                    ? 'bg-health-600 text-white border-health-600' 
                                    : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-gray-600 dark:text-gray-300 hover:border-health-300'
                                }`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Professionals Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredPros.map(pro => (
                        <div key={pro.id} className="group bg-white dark:bg-stone-800 rounded-3xl p-6 border border-stone-100 dark:border-stone-700 shadow-sm hover:shadow-xl hover:border-health-200 dark:hover:border-health-800 transition-all">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex gap-4">
                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-stone-700 dark:to-stone-600 flex items-center justify-center text-2xl font-bold text-gray-500 dark:text-gray-300 shadow-inner">
                                        {pro.image}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-health-600 transition-colors flex items-center gap-1">
                                            {pro.name}
                                            {pro.verified && <ShieldCheck size={16} className="text-health-500" />}
                                        </h3>
                                        <p className="text-sm text-health-600 dark:text-health-400 font-medium">{pro.title}</p>
                                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                            <Star size={12} className="text-yellow-400 fill-yellow-400" />
                                            <span className="font-bold text-gray-800 dark:text-gray-200">{pro.rating}</span>
                                            <span>({pro.reviewCount} reviews)</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-4 leading-relaxed">
                                {pro.bio}
                            </p>

                            <div className="flex flex-wrap gap-2 mb-6">
                                {pro.specialties.map(spec => (
                                    <span key={spec} className="px-2 py-1 bg-stone-50 dark:bg-stone-700/50 rounded-md text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 border border-stone-100 dark:border-stone-700">
                                        {spec}
                                    </span>
                                ))}
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-stone-100 dark:border-stone-700">
                                <div>
                                    <p className="text-xs text-gray-400 uppercase font-bold">Next Available</p>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-1">
                                        <Clock size={14} className="text-green-500" /> {pro.nextAvailable}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-400 uppercase font-bold">Session</p>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-white">${pro.pricePerSession}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mt-6">
                                <button
                                    onClick={() => handleProClick(pro)}
                                    className="py-2.5 rounded-xl text-sm font-bold text-gray-600 dark:text-gray-300 bg-stone-100 dark:bg-stone-700 hover:bg-stone-200 dark:hover:bg-stone-600 transition-colors"
                                >
                                    View Profile
                                </button>
                                <button
                                    onClick={() => handleBookClick(pro)}
                                    className="py-2.5 rounded-xl text-sm font-bold text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:opacity-90 transition-opacity shadow-lg shadow-gray-200 dark:shadow-none"
                                >
                                    Book Now
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* VIEW: Profile Details */}
        {view === 'profile' && selectedPro && (
             <div className="animate-fade-in-up max-w-4xl mx-auto">
                <button
                    onClick={() => setView('directory')}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-6"
                >
                    <ChevronLeft size={16} /> Back to Directory
                </button>

                <div className="glass-panel overflow-hidden rounded-[2rem] bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 shadow-xl">
                    <div className="bg-gradient-to-br from-stone-100 to-white dark:from-stone-800 dark:to-stone-900 p-8 md:p-12 relative border-b border-stone-200 dark:border-stone-800">
                        <div className="flex flex-col md:flex-row gap-8 items-start">
                            <div className="w-32 h-32 rounded-3xl bg-white dark:bg-stone-700 shadow-xl flex items-center justify-center text-4xl font-bold text-gray-400 border-4 border-white dark:border-stone-600">
                                {selectedPro.image}
                            </div>
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                            {selectedPro.name}
                                            {selectedPro.verified && <ShieldCheck size={24} className="text-health-500" />}
                                        </h1>
                                        <p className="text-xl text-health-600 dark:text-health-400 font-medium mb-2">{selectedPro.title}</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                            <MapPin size={14} /> Video Consultation • {selectedPro.languages.join(', ')}
                                        </p>
                                    </div>
                                    <div className="bg-white dark:bg-stone-800 p-4 rounded-2xl shadow-sm border border-stone-100 dark:border-stone-700 text-center min-w-[120px]">
                                        <p className="text-2xl font-bold text-gray-900 dark:text-white">${selectedPro.pricePerSession}</p>
                                        <p className="text-xs text-gray-400 uppercase font-bold">Per Session</p>
                                    </div>
                                </div>

                                <div className="flex gap-6 mt-6">
                                    <div className="flex items-center gap-2">
                                        <Star size={20} className="text-yellow-400 fill-yellow-400" />
                                        <span className="font-bold text-lg text-gray-900 dark:text-white">{selectedPro.rating}</span>
                                        <span className="text-sm text-gray-500 underline decoration-dotted">({selectedPro.reviewCount} reviews)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck size={20} className="text-trust-500" />
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Verified Credentials</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3">
                        <div className="md:col-span-2 p-8 md:p-12 border-r border-stone-100 dark:border-stone-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">About</h3>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                                {selectedPro.bio}
                            </p>

                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Education & Training</h3>
                            <ul className="space-y-3 mb-8">
                                <li className="flex gap-3">
                                    <div className="mt-1.5 w-2 h-2 rounded-full bg-health-500"></div>
                                    <span className="text-gray-700 dark:text-gray-300">{selectedPro.education}</span>
                                </li>
                                <li className="flex gap-3">
                                    <div className="mt-1.5 w-2 h-2 rounded-full bg-health-500"></div>
                                    <span className="text-gray-700 dark:text-gray-300">Board Certified in {selectedPro.specialties[0]}</span>
                                </li>
                            </ul>

                             <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Patient Reviews</h3>
                             <div className="bg-stone-50 dark:bg-stone-800/50 p-6 rounded-2xl">
                                <div className="flex gap-4 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-stone-600 flex items-center justify-center font-bold text-xs">JD</div>
                                    <div>
                                        <p className="font-bold text-sm text-gray-900 dark:text-white">Jane Doe</p>
                                        <div className="flex text-yellow-400 text-xs"><Star size={10} fill="currentColor"/><Star size={10} fill="currentColor"/><Star size={10} fill="currentColor"/><Star size={10} fill="currentColor"/><Star size={10} fill="currentColor"/></div>
                                    </div>
                                    <span className="ml-auto text-xs text-gray-400">2 days ago</span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-300 italic">"Dr. Smith was incredibly thorough and really listened to my concerns. I finally feel like I have a plan."</p>
                             </div>
                        </div>

                        <div className="p-8 bg-stone-50 dark:bg-stone-900/50">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Availability</h3>
                            <div className="space-y-4">
                                <div className="p-4 bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 shadow-sm">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-bold text-gray-900 dark:text-white">Tomorrow</span>
                                        <span className="text-xs text-gray-400">Dec 10</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button className="px-3 py-2 bg-health-50 dark:bg-health-900/20 text-health-700 dark:text-health-400 rounded-lg text-sm font-medium hover:bg-health-100 dark:hover:bg-health-900/40">2:00 PM</button>
                                        <button className="px-3 py-2 bg-health-50 dark:bg-health-900/20 text-health-700 dark:text-health-400 rounded-lg text-sm font-medium hover:bg-health-100 dark:hover:bg-health-900/40">4:30 PM</button>
                                    </div>
                                </div>
                                 <div className="p-4 bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 shadow-sm opacity-60">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-bold text-gray-900 dark:text-white">Thursday</span>
                                        <span className="text-xs text-gray-400">Dec 11</span>
                                    </div>
                                    <p className="text-xs text-center py-2">Fully Booked</p>
                                </div>
                            </div>

                            <button
                                onClick={() => handleBookClick(selectedPro)}
                                className="w-full mt-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-bold shadow-lg hover:scale-105 transition-transform"
                            >
                                Book Appointment
                            </button>
                        </div>
                    </div>
                </div>
             </div>
        )}

        {/* VIEW: Booking Flow (Mock) */}
        {view === 'booking' && selectedPro && (
            <div className="animate-fade-in-up max-w-xl mx-auto">
                <button
                    onClick={() => setView('profile')}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-6"
                >
                    <ChevronLeft size={16} /> Cancel Booking
                </button>

                <div className="glass-panel p-8 rounded-[2rem] bg-white/90 dark:bg-stone-900/90 border-stone-200 dark:border-stone-700 shadow-2xl">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Confirm Appointment</h2>

                    <div className="flex gap-4 items-center mb-8 p-4 bg-stone-50 dark:bg-stone-800 rounded-2xl border border-stone-100 dark:border-stone-700">
                        <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-stone-600 flex items-center justify-center font-bold text-gray-500 dark:text-gray-300">
                            {selectedPro.image}
                        </div>
                        <div>
                            <p className="font-bold text-gray-900 dark:text-white">{selectedPro.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{selectedPro.title}</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-bold">Date & Time</label>
                            <div className="flex items-center gap-3 p-3 border border-stone-200 dark:border-stone-700 rounded-xl bg-white dark:bg-stone-800/50 text-gray-800 dark:text-white">
                                <Calendar size={18} className="text-gray-400" />
                                <span className="font-medium">Wednesday, Dec 10 at 2:00 PM</span>
                                <span className="ml-auto text-xs text-blue-500 font-bold">Change</span>
                            </div>
                        </div>

                        <div>
                             <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-bold">Consultation Type</label>
                             <div className="grid grid-cols-2 gap-3">
                                <button className="p-3 border-2 border-health-500 bg-health-50 dark:bg-health-900/20 text-health-700 dark:text-health-400 rounded-xl font-bold flex items-center justify-center gap-2">
                                    <Video size={18} /> Video Call
                                </button>
                                <button className="p-3 border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-gray-500 dark:text-gray-400 rounded-xl font-medium flex items-center justify-center gap-2">
                                    <Phone size={18} /> Phone Call
                                </button>
                             </div>
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 font-bold">Payment</label>
                            <div className="flex items-center gap-3 p-3 border border-stone-200 dark:border-stone-700 rounded-xl bg-white dark:bg-stone-800/50 text-gray-800 dark:text-white">
                                <CreditCard size={18} className="text-gray-400" />
                                <span className="font-medium">Visa ending in 4242</span>
                                <span className="ml-auto font-bold">${selectedPro.pricePerSession}.00</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-stone-200 dark:border-stone-700">
                        <button
                            onClick={() => setView('appointments')}
                            className="w-full py-4 bg-health-600 text-white rounded-xl font-bold shadow-lg shadow-health-600/20 hover:scale-[1.02] transition-transform flex justify-center items-center gap-2"
                        >
                            Confirm & Pay <CheckCircle size={18} />
                        </button>
                        <p className="text-center text-xs text-gray-400 mt-4 flex items-center justify-center gap-1">
                            <ShieldCheck size={12} /> Secure 256-bit SSL encrypted payment
                        </p>
                    </div>
                </div>
            </div>
        )}

        {/* VIEW: My Appointments */}
        {view === 'appointments' && (
            <div className="animate-fade-in-up max-w-3xl mx-auto">
                 <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">My Appointments</h2>

                 <div className="space-y-4">
                    <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-2">Upcoming</h3>
                    {mockAppointments.filter(a => a.status === 'upcoming').map(appt => (
                        <div key={appt.id} className="bg-white dark:bg-stone-800 rounded-2xl p-6 border-l-4 border-l-green-500 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
                            <div className="flex gap-4 items-center">
                                <div className="w-12 h-12 rounded-full bg-stone-100 dark:bg-stone-700 flex items-center justify-center font-bold text-gray-500 dark:text-gray-400">
                                    {appt.professionalImage}
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg text-gray-900 dark:text-white">{appt.professionalName}</h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{appt.professionalTitle}</p>
                                    <div className="flex items-center gap-3 mt-1 text-sm font-medium text-gray-800 dark:text-gray-200">
                                        <span className="flex items-center gap-1"><Calendar size={14} className="text-health-500" /> {appt.date}</span>
                                        <span className="flex items-center gap-1"><Clock size={14} className="text-health-500" /> {appt.time}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 w-full md:w-auto">
                                <button className="flex-1 md:flex-none px-4 py-2 bg-health-600 text-white rounded-lg font-bold shadow-sm hover:bg-health-700 transition-colors flex items-center justify-center gap-2">
                                    <Video size={16} /> Join Call
                                </button>
                                <button className="px-4 py-2 border border-stone-200 dark:border-stone-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium hover:bg-stone-50 dark:hover:bg-stone-700">
                                    Reschedule
                                </button>
                            </div>
                        </div>
                    ))}

                    <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-2 mt-8">Past</h3>
                    {mockAppointments.filter(a => a.status === 'completed').map(appt => (
                        <div key={appt.id} className="bg-white dark:bg-stone-800/60 rounded-2xl p-6 border border-stone-100 dark:border-stone-800 flex flex-col md:flex-row justify-between items-center gap-4 opacity-70 hover:opacity-100 transition-opacity">
                            <div className="flex gap-4 items-center">
                                <div className="w-12 h-12 rounded-full bg-stone-100 dark:bg-stone-700 flex items-center justify-center font-bold text-gray-400">
                                    {appt.professionalImage}
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg text-gray-700 dark:text-gray-300">{appt.professionalName}</h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{appt.professionalTitle}</p>
                                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        <span>{appt.date}</span>
                                        <span>• {appt.type} consultation</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 w-full md:w-auto">
                                <button className="px-4 py-2 bg-stone-100 dark:bg-stone-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium hover:bg-stone-200 dark:hover:bg-stone-600">
                                    View Notes
                                </button>
                                <button className="px-4 py-2 bg-trust-50 dark:bg-trust-900/20 text-trust-600 dark:text-trust-400 rounded-lg font-medium hover:bg-trust-100 dark:hover:bg-trust-900/40">
                                    Book Again
                                </button>
                            </div>
                        </div>
                    ))}
                 </div>
            </div>
        )}

      </div>
    </div>
  );
};

export default ProfessionalServices;
